<template>
  <AppBar />
  <v-main>
    <v-container style="max-width: 850px" class="py-6">
      <!-- Hero Section: Bio + Avatar -->
      <v-row align="center" class="mb-8">
        <v-col cols="12" md="7" order="2" order-md="1">
          <p class="name text-center text-md-left">Shr-Ruei (Ray) Tsai 蔡師睿</p>
          <p class="tagline text-center text-md-left">
            MSCV @ Carnegie Mellon University · Robotics Institute
          </p>
          <div class="bio-text">
            <p>
              Hi! I'm a graduate student in Computer Vision at
              <strong>Carnegie Mellon University's Robotics Institute</strong>.
              Before that, I received my B.S. in Computer Science from
              <strong>National Yang Ming Chiao Tung University</strong>,
              where I was advised by
              <a href="https://yulunalexliu.github.io/" target="_blank">Prof. Yu-Lun Liu</a>
              at the Computational Photography Lab.
            </p>
            <p>
              My research sits at the intersection of
              <span class="hl">Computer Vision</span> and
              <span class="hl">Robot Learning</span>, with a focus on
              <span class="hl">robot perception</span>. Past work spans
              3D scene reconstruction, diffusion-based image restoration,
              and vision-language robotic navigation.
            </p>
          </div>
          <p class="text-center text-md-left mt-6 social-row">
            <Icon_link href="mailto:srtsai.1026@gmail.com" icon_name="mdi-gmail" />
            <span class="icon-spacer"></span>
            <Icon_link href="https://github.com/Ray-1026" icon_name="mdi-github" />
            <span class="icon-spacer"></span>
            <Icon_link
              href="https://www.linkedin.com/in/shr-ruei-tsai"
              icon_name="mdi-linkedin"
            />
            <!-- <span class="icon-spacer"></span>
            <Icon_link
              href="https://www.facebook.com/people/%E8%94%A1%E5%B8%AB%E7%9D%BF/pfbid02w7FDGTNJM18k7f4YsFMWcPCP9cHbR49qNrfZwFKMbEC4HhRfzaBRWMNs7mzh31qvl/?mibextid=ZbWKwL"
              icon_name="mdi-facebook"
            /> -->
            <span class="icon-spacer"></span>
            <Icon_link href="https://scholar.google.com/citations?user=uVE9-3sAAAAJ&hl=en" icon_name="mdi-book-open-page-variant" />
          </p>
        </v-col>
        <v-col cols="12" md="5" order="1" order-md="2" class="text-center">
          <div class="avatar-wrapper">
            <v-avatar :image="require('../assets/ray1.png')" size="200"></v-avatar>
          </div>
        </v-col>
      </v-row>

      <!-- Experience Section -->
      <div class="section-block">
        <h2 class="section-title">
          # Experience
          <router-link to="/experience" class="more-link">More details →</router-link>
        </h2>

        <!-- Education -->
        <v-card class="exp-card mb-4" variant="outlined" color="grey-darken-3">
          <v-card flat color="transparent">
            <v-container class="py-3">
              <div class="d-flex align-center">
                <v-icon class="mr-3" color="primary" size="28">mdi-school</v-icon>
                <div class="text-h6 font-weight-light card-header-title">Education</div>
              </div>
            </v-container>
          </v-card>
          <v-card-text class="py-0 pb-2">
            <v-timeline density="compact" align="start">
              <v-timeline-item
                v-for="(education, index) in educations"
                :key="'edu-' + index"
                dot-color="primary"
                :icon="education.logo ? undefined : education.icon"
                size="small"
              >
                <template v-if="education.logo" #icon>
                  <img :src="education.logo" :alt="education.school" class="timeline-logo" />
                </template>
                <div class="d-flex pt-1">
                  <div class="timeline-duration me-4">
                    <strong>{{ education.duration }}</strong>
                  </div>
                  <div>
                    <strong class="timeline-title">{{ education.school }}</strong>
                    <div class="text-caption timeline-subtitle">{{ education.degree }}</div>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>

        <!-- Research Experience -->
        <v-card class="exp-card mb-4" variant="outlined" color="grey-darken-3">
          <v-card flat color="transparent">
            <v-container class="py-3">
              <div class="d-flex align-center">
                <v-icon class="mr-3" color="info" size="28">mdi-flask</v-icon>
                <div class="text-h6 font-weight-light card-header-title">Research Experience</div>
              </div>
            </v-container>
          </v-card>
          <v-card-text class="py-0 pb-2">
            <v-timeline density="compact" align="start">
              <v-timeline-item
                v-for="(exp, index) in researchExperience"
                :key="'research-' + index"
                dot-color="info"
                :icon="exp.logo ? undefined : exp.icon"
                size="small"
              >
                <template v-if="exp.logo" #icon>
                  <img :src="exp.logo" :alt="exp.organization" class="timeline-logo" />
                </template>
                <div class="d-flex pt-1">
                  <div class="timeline-duration me-4">
                    <strong>{{ exp.duration }}</strong>
                  </div>
                  <div>
                    <strong class="timeline-title">{{ exp.title }}</strong>
                    <div class="text-caption timeline-subtitle">{{ exp.organization }}</div>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>

        <!-- Industry Experience -->
        <v-card class="exp-card mb-4" variant="outlined" color="grey-darken-3">
          <v-card flat color="transparent">
            <v-container class="py-3">
              <div class="d-flex align-center">
                <v-icon class="mr-3" color="success" size="28">mdi-briefcase</v-icon>
                <div class="text-h6 font-weight-light card-header-title">Industry Experience</div>
              </div>
            </v-container>
          </v-card>
          <v-card-text class="py-0 pb-2">
            <v-timeline density="compact" align="start">
              <v-timeline-item
                v-for="(exp, index) in industryExperience"
                :key="'industry-' + index"
                dot-color="success"
                :icon="exp.logo ? undefined : exp.icon"
                size="small"
              >
                <template v-if="exp.logo" #icon>
                  <img :src="exp.logo" :alt="exp.organization" class="timeline-logo" />
                </template>
                <div class="d-flex pt-1">
                  <div class="timeline-duration me-4">
                    <strong>{{ exp.duration }}</strong>
                  </div>
                  <div>
                    <strong class="timeline-title">{{ exp.title }}</strong>
                    <div class="text-caption timeline-subtitle">{{ exp.organization }}</div>
                  </div>
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-card-text>
        </v-card>
      </div>

      <!-- Publications Section -->
      <div class="section-block">
        <h2 class="section-title"># Publications
          <!-- <router-link to="/publications" class="more-link">Publication details →</router-link> -->
        </h2>

        <div
          v-for="(pub, index) in mypublications"
          :key="'pub-' + index"
          class="research-card"
        >
          <v-row class="research-item" align="center">
            <v-col cols="12" sm="3" class="text-center">
              <video
                v-if="pub.media.type === 'video'"
                :src="pub.media.src"
                class="research-media"
                muted
                loop
                autoplay
                playsinline
              ></video>
              <img
                v-else
                :src="pub.media.src"
                class="research-media research-img"
                :alt="pub.title"
              />
            </v-col>
            <v-col cols="12" sm="9">
              <a :href="pub.links[0].url" target="_blank">
                <span class="paper-title">{{ pub.title }}</span>
              </a>
              <br />
              <span class="authors" v-html="pub.authors"></span>
              <br />
              <span class="venue-badge">{{ pub.venue }}</span>
              <br />
              <span class="paper-links">
                <template v-for="(link, li) in pub.links" :key="li">
                  <span v-if="li > 0" class="link-sep">·</span>
                  <a :href="link.url" target="_blank">{{ link.label }}</a>
                </template>
              </span>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- Projects Section -->
      <div class="section-block">
        <h2 class="section-title"># Selected Projects 
          <router-link to="/projects" class="more-link">More →</router-link>
        </h2>

        <div
          v-for="p in selectedProject"
          :key="p.title"
          class="research-card"
        >
          <v-row class="research-item" align="center">
            <v-col cols="12" sm="3" class="text-center">
              <img :src="p.img" class="research-media research-img" :alt="p.title" />
            </v-col>
            <v-col cols="12" sm="9">
              <a :href="p.link" target="_blank">
                <span class="paper-title">{{ p.title }}</span>
              </a>
              <br />
              <span class="venue-badge">{{ p.language }}</span>
              <span class="venue-badge" style="margin-left: 6px">{{ p.time }}</span>
              <p class="paper-desc">{{ p.text }}</p>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import { defineComponent } from "vue";
import AppBar from "../components/AppBar.vue";
import Icon_link from "../components/Icon.vue";
import { mypublications } from "../data.js";

export default defineComponent({
  name: "HomeView",
  components: {
    AppBar,
    Icon_link,
  },
  data() {
    return {
      mypublications,
      educations: [
        {
          school: "Carnegie Mellon University",
          degree: "Master of Science in Computer Vision",
          duration: "Aug. 2026 - Dec. 2027",
          icon: "mdi-school",
        },
        {
          school: "National Yang Ming Chiao Tung University (NYCU)",
          degree: "Bachelor of Science in Computer Science",
          duration: "Sep. 2021 - Jun. 2025",
          icon: "mdi-school",
        },
      ],
      researchExperience: [
        {
          title: "Undergraduate Research Assistant",
          organization:
            "Computational Photography Lab, NYCU (Prof. Yu-Lun Liu)",
          duration: "Jul. 2023 - Present",
          icon: "mdi-flask",
        },
        {
          title: "Research Assistant",
          organization: "Metals & Applications Laboratory, NYCU (Prof. E-Wen Huang)",
          duration: "Oct. 2023 - Oct. 2024",
          icon: "mdi-flask",
        },
      ],
      industryExperience: [
        {
          title: "Surveillance Product Developer Intern — Video Deep Learning",
          organization: "Synology",
          duration: "Oct. 2025 - May. 2026",
          icon: "mdi-briefcase",
        },
        {
          title: "3D Multimedia System Development Intern",
          organization: "Industrial Technology Research Institute (ITRI)",
          duration: "Jun. 2025 - Oct. 2025",
          icon: "mdi-briefcase",
        },
        {
          title: "AI Engineer Intern",
          organization: "Big Data Mobile corp.",
          duration: "Jul. 2024 - Sep. 2024",
          icon: "mdi-briefcase",
        },
      ],
      selectedProject: [
        {
            title: 'Optimizing the Traveling Salesman Problem with Parallel Algorithms',
            time: 'Dec, 2024',
            language: 'NYCU CSIC30045: Parallel Programming',
            text: "Parallelized metaheuristic algorithms for the Traveling Salesman Problem (TSP), achieving a 37.8× speedup with under 1% loss in solution quality compared to the serial implementation.",
            link: 'https://github.com/Shukkai/Parallel-Programming-Final',
            img: 'static/pp.png',
        },
        {
            title: 'CR-NeRF via Depth Guidance',
            time: 'Jun, 2024',
            language: 'NYCU CSIC30140: Computer Vision',
            text: 'Integrates monocular depth maps into NeRF for enhanced occlusion-free novel view synthesis, achieving an average 0.4 dB PSNR improvement over the ICCV 2023 SOTA method across three scenes.',
            link: 'https://github.com/Ray-1026/CR-NeRF-via-Depth-Guidance',
            img: 'static/cr_nerf.gif',
        },
      ],
    };
  },
  // computed: {
  //   getProjects() {
  //     return this.myprojects.slice();
  //   },
  // },
});
</script>

<style scoped>
/* ── Name & Bio ── */
.name {
  font-size: 34px;
  font-weight: 800;
  margin-bottom: 6px;
  background: linear-gradient(135deg, lightskyblue, #a78bfa);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.2px;
}

.tagline {
  font-size: 14px;
  font-weight: 500;
  color: #9aa4b2;
  margin-bottom: 20px;
  letter-spacing: 0.3px;
}

.bio-text {
  font-size: 15px;
  line-height: 1.75;
  color: #d0d0d0;
}
.bio-text p {
  margin-bottom: 14px;
}
.bio-text p:last-child {
  margin-bottom: 0;
}
.bio-text strong {
  color: #e8e8e8;
  font-weight: 600;
}
.hl {
  color: lightskyblue;
  font-weight: 600;
}

.social-row {
  margin-top: 20px !important;
}

/* ── Avatar ── */
.avatar-wrapper {
  display: inline-block;
  padding: 4px;
  border-radius: 50%;
  background: linear-gradient(135deg, lightskyblue, #a78bfa);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.avatar-wrapper:hover {
  transform: scale(1.05);
  box-shadow: 0 0 24px rgba(135, 206, 250, 0.4);
}

/* ── Social Icons ── */
.icon-spacer {
  width: 24px;
  display: inline-block;
}

/* ── Links ── */
a {
  color: #64b5f6;
  text-decoration: none;
  transition: color 0.2s ease;
}
a:hover {
  color: #f09228;
}

/* ── Sections ── */
.section-block {
  margin-top: 40px;
}

.section-title {
  font-size: 24px;
  font-weight: 700;
  color: lightskyblue;
  margin-bottom: 20px;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(135, 206, 250, 0.2);
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.more-link {
  font-size: 14px;
  font-weight: 400;
  color: #888 !important;
  transition: color 0.2s ease;
}
.more-link:hover {
  color: lightskyblue !important;
}

/* ── Experience Cards ── */
.exp-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 12px !important;
}
.exp-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(135, 206, 250, 0.1);
}

.card-header-title {
  color: lightskyblue;
}

.timeline-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 50%;
  background: #fff;
  padding: 2px;
  box-sizing: border-box;
}

.timeline-duration {
  min-width: 150px;
  font-size: 13px;
  color: #00ced1;
}

.timeline-title {
  color: #eee;
}

.timeline-subtitle {
  color: #aaa;
}

/* ── Research Cards ── */
.research-card {
  padding: 16px 12px;
  margin-bottom: 8px;
  border-radius: 12px;
  border: 1px solid transparent;
  transition: background 0.25s ease, border-color 0.25s ease, transform 0.2s ease;
}
.research-card:hover {
  background: rgba(135, 206, 250, 0.04);
  border-color: rgba(135, 206, 250, 0.15);
  transform: translateY(-2px);
}

.research-item {
  margin-bottom: 0;
}

.research-media {
  width: 100%;
  max-width: 180px;
  height: auto;
  object-fit: cover;
  border-radius: 8px;
}

.research-img {
  object-fit: contain;
}

.paper-title {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.5;
}

.authors {
  font-size: 14px;
  color: #bbb;
  line-height: 1.6;
}

.venue-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: lightskyblue;
  background: rgba(135, 206, 250, 0.12);
  padding: 2px 10px;
  border-radius: 12px;
  margin-top: 4px;
}

.paper-links {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-size: 13px;
}
.paper-links a {
  color: #64b5f6;
}
.paper-links a:hover {
  color: #f09228;
}

.link-sep {
  color: #555;
  margin: 0 2px;
}

.paper-desc {
  margin-top: 6px;
  font-size: 13px;
  color: #999;
  line-height: 1.6;
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .name {
    font-size: 26px;
  }

  .icon-spacer {
    width: 16px;
  }

  .research-media {
    max-width: 140px;
  }

  .timeline-duration {
    min-width: 120px;
    font-size: 12px;
  }

  .section-title {
    flex-direction: column;
    gap: 4px;
  }
}
</style>